import {
  collection,
  doc,
  onSnapshot,
  query,
  updateDoc,
  where,
  type Unsubscribe,
} from 'firebase/firestore'
import { firestoreDb } from '../firebase'
import { getDebugProofsForItem, isNoSignInPreviewActive, recordDebugProof, subscribeDebugProofStore } from '../../debug'
import type { ChecklistAttachment, ChecklistItem, ChecklistWorkflowStatus } from '../../types/schema'
import { resolveAllowedTransitions } from './helperTaskService'
import {
  queueChecklistComment,
  queueChecklistProof,
  queueChecklistToggle,
} from './queueService'
import type { HelperChecklistWorkflowStatus, MobileOutboxActorRole } from './helperOutboxDb'
import { subscribeChecklistItemProjection } from '../checklistReader'
import { drainOutbox, isOnline, waitForMutationTerminalState } from './syncWorker'
import type { HelperMutationRecord } from './helperOutboxDb'

export interface HelperChecklistAttachment {
  id: string
  name: string
  contentType: string
  size: number
  downloadUrl: string
  storagePath: string
  uploadedById: string
  uploadedByName: string
  uploadedAt: string
}

export interface HelperChecklistComment {
  id: string
  body: string
  actorId: string
  actorName: string
  createdAt: number | null
  editedAt: number | null
  deletedAt: number | null
  deletedBy: string | null
  attachments: HelperChecklistAttachment[]
  userAvatarUrl: string | null
}

export interface HelperChecklistProof {
  id: string
  fileUrl: string | null
  fileName: string | null
  contentType: string | null
  storagePath: string | null
  uploadedById: string | null
  uploadedByName: string | null
  uploadedAt: number | null
}

export const projectProofsFromChecklistItem = (
  item: Pick<
    ChecklistItem,
    | 'id'
    | 'proofFileUrl'
    | 'proofFileName'
    | 'proofFileStoragePath'
    | 'proofUploadedById'
    | 'proofUploadedByName'
    | 'proofUploadedAt'
  > | null,
): HelperChecklistProof[] => {
  if (!item?.proofFileUrl) return []
  return [
    {
      id: typeof item.proofFileStoragePath === 'string' ? item.proofFileStoragePath : item.id,
      fileUrl: typeof item.proofFileUrl === 'string' ? item.proofFileUrl : null,
      fileName: typeof item.proofFileName === 'string' ? item.proofFileName : null,
      contentType: null,
      storagePath: typeof item.proofFileStoragePath === 'string' ? item.proofFileStoragePath : null,
      uploadedById: typeof item.proofUploadedById === 'string' ? item.proofUploadedById : null,
      uploadedByName: typeof item.proofUploadedByName === 'string' ? item.proofUploadedByName : null,
      uploadedAt: millis(item.proofUploadedAt),
    },
  ]
}

const millis = (value: unknown): number | null => {
  if (!value) return null
  if (typeof value === 'object' && 'toMillis' in (value as Record<string, unknown>)) {
    try {
      return (value as { toMillis: () => number }).toMillis()
    } catch {
      return null
    }
  }
  if (typeof value === 'number') return value
  if (typeof value === 'string') {
    const parsed = Date.parse(value)
    return Number.isNaN(parsed) ? null : parsed
  }
  return null
}

const mapChecklistAttachment = (value: unknown): HelperChecklistAttachment | null => {
  if (!value || typeof value !== 'object') return null
  const attachment = value as ChecklistAttachment
  const id = typeof attachment.id === 'string' && attachment.id.trim().length > 0
    ? attachment.id
    : typeof attachment.storagePath === 'string'
      ? attachment.storagePath
      : null
  if (!id) return null
  return {
    id,
    name: typeof attachment.name === 'string' ? attachment.name : 'Attachment',
    contentType: typeof attachment.contentType === 'string' ? attachment.contentType : 'application/octet-stream',
    size: typeof attachment.size === 'number' ? attachment.size : 0,
    downloadUrl: typeof attachment.downloadUrl === 'string' ? attachment.downloadUrl : '',
    storagePath: typeof attachment.storagePath === 'string' ? attachment.storagePath : '',
    uploadedById: typeof attachment.uploadedById === 'string' ? attachment.uploadedById : '',
    uploadedByName: typeof attachment.uploadedByName === 'string' ? attachment.uploadedByName : '',
    uploadedAt: typeof attachment.uploadedAt === 'string' ? attachment.uploadedAt : '',
  }
}

export const subscribeChecklistComments = (
  itemId: string,
  workspaceId: string,
  onChange: (items: HelperChecklistComment[]) => void,
  onError?: (error: Error) => void,
): Unsubscribe => {
  if (!workspaceId || !itemId || isNoSignInPreviewActive()) {
    onChange([])
    return () => {}
  }
  const q = query(
    collection(firestoreDb, 'checklist_comments'),
    where('workspaceId', '==', workspaceId),
    where('checklistItemId', '==', itemId),
  )
  return onSnapshot(
    q,
    (snapshot) => {
      const items = snapshot.docs.map((entry) => {
        const data = entry.data() as Record<string, unknown>
        return {
          id: entry.id,
          body: typeof data.body === 'string'
            ? data.body
            : typeof data.text === 'string'
              ? data.text
              : typeof data.textContent === 'string'
                ? data.textContent
                : '',
          actorId: typeof data.userId === 'string' ? data.userId : (typeof data.actorId === 'string' ? data.actorId : ''),
          actorName: typeof data.userName === 'string' ? data.userName : (typeof data.actorName === 'string' ? data.actorName : 'Helper'),
          createdAt: millis(data.createdAt) ?? millis(data.timestamp),
          editedAt: millis(data.editedAt),
          deletedAt: millis(data.deletedAt),
          deletedBy: typeof data.deletedBy === 'string' ? data.deletedBy : null,
          attachments: Array.isArray(data.attachments)
            ? data.attachments.map(mapChecklistAttachment).filter((attachment): attachment is HelperChecklistAttachment => Boolean(attachment))
            : [],
          userAvatarUrl: typeof data.userAvatarUrl === 'string' ? data.userAvatarUrl : null,
        }
      })
      items.sort((a, b) => (b.createdAt ?? 0) - (a.createdAt ?? 0))
      onChange(items)
    },
    (err) => {
      const error = err instanceof Error ? err : new Error(String(err))
      if (onError) {
        onError(error)
      } else {
        console.error('[subscribeChecklistComments] snapshot error', error)
      }
    },
  )
}

/**
 * Proofs are stored as direct fields on the checklist_items document
 * (`proofFileUrl`, `proofFileName`, `proofFileStoragePath`, `proofUploadedAt`).
 * This subscription reads those fields and returns a single-element list when
 * a proof is present, or an empty list otherwise — keeping a list shape so the
 * UI can iterate without a special case.
 */
export const subscribeChecklistProofs = (
  itemId: string,
  onChange: (items: HelperChecklistProof[]) => void,
): Unsubscribe => {
  if (isNoSignInPreviewActive()) {
    onChange(getDebugProofsForItem(itemId))
    return subscribeDebugProofStore(() => onChange(getDebugProofsForItem(itemId)))
  }
  return subscribeChecklistItemProjection(
    { itemId },
    (item) => {
      onChange(projectProofsFromChecklistItem(item))
    },
  )
}

const HELPER_OUTBOX_STATUSES = new Set<HelperChecklistWorkflowStatus>([
  'ASSIGNED',
  'ACCEPTED',
  'ASSESSING',
  'ONGOING',
  'COMPLETED',
  'NOT_REQUIRED',
  'POSTPONED',
])

export interface TransitionContext {
  item: Pick<
    ChecklistItem,
    'id' | 'workspaceId' | 'parentContainerId' | 'workflowStatus' | 'isCompleted'
  > & {
    allowedTransitions?: Partial<Record<ChecklistWorkflowStatus, ChecklistWorkflowStatus[]>> | null
    updatedAtIso?: string | null
  }
  actor: { id: string; name: string }
  next: ChecklistWorkflowStatus
  /** The book page (occurrence) this transition applies to. Recurring
   *  items (DAILY / WEEKLY) MUST supply this so a queue drained after
   *  midnight still lands on the intended date/week. INDIVIDUAL: omit,
   *  the drain layer uses the sole 'single' page anyway. */
  pageKey?: string | null
  /** Which shell is queuing this — see MobileOutboxActorRole. Member paths
   *  must pass 'MEMBER' so the sync worker bypasses helper-visibility masks
   *  and audit rows are attributed to the right role. Absent → 'HELPER'. */
  actorRole?: MobileOutboxActorRole
}

export const canHelperTransition = (
  item: TransitionContext['item'],
  next: ChecklistWorkflowStatus,
): boolean => resolveAllowedTransitions(item).includes(next)

export const submitHelperTransition = async (ctx: TransitionContext): Promise<void> => {
  if (!canHelperTransition(ctx.item, ctx.next)) {
    throw new Error(`Transition not allowed from ${ctx.item.workflowStatus ?? 'ASSIGNED'} to ${ctx.next}`)
  }
  if (!HELPER_OUTBOX_STATUSES.has(ctx.next as HelperChecklistWorkflowStatus)) {
    throw new Error(`Unsupported helper status: ${ctx.next}`)
  }
  await queueChecklistToggle({
    workspaceId: ctx.item.workspaceId,
    goalId: ctx.item.parentContainerId,
    taskId: null,
    subTaskId: null,
    checklistItemId: ctx.item.id,
    isCompleted: ctx.next === 'COMPLETED',
    workflowStatus: ctx.next as HelperChecklistWorkflowStatus,
    actorId: ctx.actor.id,
    actorName: ctx.actor.name,
    assignedAt: ctx.item.updatedAtIso ?? null,
    pageKey: ctx.pageKey ?? null,
    actorRole: ctx.actorRole,
  })
  // Kick the drain immediately so the write hits Firestore in a few
  // hundred ms instead of waiting up to POLL_MS (30 s) — otherwise the
  // helper/member modals sit on a queued mutation with no visible change,
  // and the calling shell perceives the transition as "totally broken".
  void drainOutbox()
}

export const submitHelperComment = async (params: {
  item: Pick<ChecklistItem, 'id' | 'workspaceId' | 'parentContainerId'> & { updatedAtIso?: string | null }
  actor: { id: string; name: string }
  body: string
  pageKey?: string | null
  actorRole?: MobileOutboxActorRole
  waitForPersist?: boolean
}): Promise<HelperMutationRecord | void> => {
  const trimmed = params.body.trim()
  if (!trimmed) return
  const record = await queueChecklistComment({
    workspaceId: params.item.workspaceId,
    parentContainerId: params.item.parentContainerId,
    checklistItemId: params.item.id,
    body: trimmed,
    actorId: params.actor.id,
    actorName: params.actor.name,
    assignedAt: params.item.updatedAtIso ?? null,
    pageKey: params.pageKey ?? null,
    actorRole: params.actorRole,
  })
  void drainOutbox()
  if (params.waitForPersist) {
    if (isOnline()) {
      const outcome = await waitForMutationTerminalState(record.id)
      if (outcome.status === 'FAILED') {
        throw new Error(outcome.error || 'Failed to add comment.')
      }
    }
  }
  return record
}

export const editHelperComment = async (params: {
  commentId: string
  actorId: string
  body: string
}): Promise<void> => {
  const trimmed = params.body.trim()
  if (!trimmed) throw new Error('Comment cannot be empty.')
  await updateDoc(doc(firestoreDb, 'checklist_comments', params.commentId), {
    body: trimmed,
    text: trimmed,
    textContent: trimmed,
    editedAt: new Date().toISOString(),
    deletedAt: null,
    deletedBy: null,
  })
}

export const deleteHelperComment = async (params: {
  commentId: string
  actorId: string
}): Promise<void> => {
  await updateDoc(doc(firestoreDb, 'checklist_comments', params.commentId), {
    body: '',
    text: '',
    textContent: '',
    deletedAt: new Date().toISOString(),
    deletedBy: params.actorId,
  })
}

export const submitHelperProof = async (params: {
  item: Pick<ChecklistItem, 'id' | 'workspaceId'> & { title?: string; updatedAtIso?: string | null }
  actor: { id: string; name: string }
  file: File
  pageKey?: string | null
  actorRole?: MobileOutboxActorRole
}): Promise<void> => {
  if (isNoSignInPreviewActive()) {
    recordDebugProof({
      workspaceId: params.item.workspaceId,
      itemId: params.item.id,
      itemTitle: params.item.title ?? 'Task',
      actorId: params.actor.id,
      actorName: params.actor.name,
      file: params.file,
    })
    return
  }
  await queueChecklistProof(
    {
      workspaceId: params.item.workspaceId,
      checklistItemId: params.item.id,
      fileName: params.file.name,
      contentType: params.file.type || 'application/octet-stream',
      actorId: params.actor.id,
      actorName: params.actor.name,
      assignedAt: params.item.updatedAtIso ?? null,
      pageKey: params.pageKey ?? null,
      actorRole: params.actorRole,
    },
    params.file,
  )
  void drainOutbox()
}
